import moment from 'moment';
import fb from '@/firebaseConfig';
import localStore from '@/utils/LocalStore';
import router from '@/router';
import analytics from '@/utils/Analytics';
import { sortByTitle } from '@/helpers/contentHelpers';

/*
 * NON-SUBSCRIBERS:
 * - Can't create stacks or edit default stacks
 * - For default stacks list, will see all stacks including premium stacks
 * - While swiping through stacks, won't see premium stacks
 *
 * SUBSCRIBERS:
 * - Can create their own stacks ("original") or edit default stacks ("adapted")
 * - Adapted stacks also show in "My Stacks", along with original
 * - Default stacks list includes free & premium stacks, excluding adapted stacks
 *
 * Default vs User Stack IDs Examples:
 * - Default stack: "cozy"
 * - User stack adapted from default: "stack-cozy"
 * - Original user stack: "stack-202006021829143"
 */

// TODO Proper error handling for DB actions?

/*

  STATE

 */

const state = {
  defaultStacks: {},
  defaultStackIds: [], // Only stacks user has access to
  defaultStackIdsForList: [], // Includes premium stacks
  userStacks: {},
  recentStackIds: [],
  isLoaded: false,
};

/*

  GETTERS

 */

const getters = {
  allStacks: state => {
    const { defaultStacks, userStacks } = state;
    const stacks = {};
    Object.keys(defaultStacks).forEach(id => {
      stacks[id] = defaultStacks[id];
    });
    Object.keys(userStacks).forEach(id => {
      stacks[id] = userStacks[id];
    });
    return stacks;
  },

  // Returns array of default stack IDs,
  // filtering out stacks the user has edited to make their own
  // so user doesn't see same stack twice
  // For non-subscribers users, returns same list of non-premium default IDs
  filteredDefaultStackIds: state => {
    const { defaultStackIds, userStacks } = state;
    return defaultStackIds.filter(id => !userStacks[`stack-${id}`]);
  },

  // Returns array user stack IDs sorted by title
  sortedUserStackIds: (state, getters) => {
    const { userStacks } = state;
    const { allStacks } = getters;
    const unsortedIds = Object.keys(userStacks);
    return sortByTitle(allStacks, unsortedIds);
  },
};

/*

  ACTIONS

 */

const actions = {
  init({ dispatch }, { isMember }) {
    // const { isLoaded } = this;

    // if (!isLoaded) {
    dispatch('getDefaultStacks', isMember);
    dispatch('loadRecentIds');
    // }
  },

  async getDefaultStacks({ commit }, isMember) {
    // const { isMember } = rootState.user;

    try {
      const docs = await fb.stacksCollection.orderBy('title').get();
      const stacksData = {};
      const stacksIds = [];
      const stacksIdsForList = [];

      docs.forEach(doc => {
        const { id } = doc;
        const data = doc.data();
        const userCanAccess = !data.isPremium || isMember;

        stacksData[id] = {
          ...data,
          userCanAccess,
        };
        stacksIdsForList.push(id);
        if (userCanAccess) stacksIds.push(id);
      });
      commit('setDefaultStacks', { stacksData, stacksIds, stacksIdsForList });
    } catch (error) {
      console.log('Error getting audio', error);
    }
  },

  addCardToStack({ dispatch, rootState, getters }, stackId) {
    const cardId = rootState.ui.currentId;
    const stack = getters.allStacks[stackId];
    const updatedStack = { ...stack };
    updatedStack.cardIds = [...stack.cardIds, cardId];
    analytics.stackAction('add_card');
    dispatch('updateStack', updatedStack);
    dispatch(
      'ui/updateSnack',
      { text: `Card added to "${stack.title || 'playlist'}"`, quick: true },
      { root: true }
    );
  },

  // stack fields: { id, cardIds, title, lastUpdatedAt }
  updateStack({ commit, dispatch }, data) {
    const stack = { ...data };
    const { id = '' } = stack;

    const isNewOriginal = !id;
    const isNewAdapted = id.substring(0, 5) !== 'stack'; // Adapted from default stack
    let analyticsAction = 'edit';
    if (isNewOriginal) {
      stack.id = `stack-${moment().format('YYYYMMDDHHmmssS')}`;
      analyticsAction = 'create_original';
    } else if (isNewAdapted) {
      stack.id = `stack-${id}`;
      analyticsAction = 'create_adapted';
    }
    if (stack.title === ' ') {
      stack.title = '';
    }
    stack.lastUpdatedAt = fb.currentTimestamp();
    commit('updateStack', stack);
    dispatch('saveStackToDB', stack);
    analytics.stackAction(analyticsAction);

    if (isNewAdapted) {
      router.replace({ name: 'Stacks' });
      dispatch(
        'ui/updateSnack',
        { text: 'Playlist saved to "My Playlists"', quick: true },
        { root: true }
      );
    } else {
      dispatch('ui/updateSnack', { text: 'Playlist saved', quick: true }, { root: true });
    }
  },

  saveStackToDB({ rootState }, stack) {
    const userId = rootState.user.uid;
    const stackId = stack.id;
    fb.usersCollection
      .doc(userId)
      .update({ [`stacks.${stackId}`]: stack })
      .catch(error => {
        console.log('Error updating playlist', error);
      });
  },

  deleteStack({ commit, dispatch, rootState }, stackId) {
    const userId = rootState.user.uid;
    if (stackId) {
      commit('removeStack', stackId);
      fb.usersCollection
        .doc(userId)
        .update({ [`stacks.${stackId}`]: fb.dbFieldValue.delete() })
        .then(() => {
          dispatch('ui/updateSnack', { text: 'Playlist deleted', quick: true }, { root: true });
        })
        .catch(error => {
          console.log('Error deleting playlist', error);
        });
      analytics.stackAction('delete');
    }
  },

  async loadRecentIds({ commit, state }) {
    const savedIds = await localStore.getItem('recentStackIds');
    if (!state.recentStackIds.length) {
      commit('setRecentStackIds', savedIds || []);
    }
  },

  async addToRecentIds({ commit, state }, stackId) {
    let recentIds = [...state.recentStackIds];
    if (!recentIds.length) {
      recentIds = (await localStore.getItem('recentStackIds')) || [];
    }
    const index = recentIds.indexOf(stackId);
    if (index > -1) recentIds.splice(index, 1);
    const newRecentIds = [stackId, ...recentIds].slice(0, 8);
    commit('setRecentStackIds', newRecentIds);
    localStore.setItem('recentStackIds', newRecentIds);
  },

  clearRecentIds() {
    localStore.setItem('recentStackIds', []);
  },
};

/*

  MUTATIONS

 */

const mutations = {
  setUserStacks(state, data) {
    if (data) {
      const userStacks = { ...data };
      Object.keys(userStacks).forEach(id => {
        userStacks[id].userCanAccess = true;
        userStacks[id].isPremium = true;
      });
      state.userStacks = userStacks;
    }
  },

  setDefaultStacks(state, data) {
    const { stacksData, stacksIds, stacksIdsForList } = data;
    state.defaultStacks = stacksData;
    state.defaultStackIds = stacksIds;
    state.defaultStackIdsForList = stacksIdsForList;
    state.isLoaded = true;
  },

  // Any stack a user updates becomes a user stack
  updateStack(state, stack) {
    const { id } = stack;
    const userStacks = { ...state.userStacks };
    userStacks[id] = stack;
    state.userStacks = userStacks;
  },

  removeStack(state, stackId) {
    const userStacks = { ...state.userStacks };
    delete userStacks[stackId];
    state.userStacks = userStacks;
  },

  setRecentStackIds(state, recentIds) {
    state.recentStackIds = recentIds;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
